
import { Component, Prop, Vue } from "vue-property-decorator";
import { SMUser, SMCases } from "@/store";
import { API_ROUTES } from "@/utils/API";
import { API_TReport } from "@/models/report";

import { EMPLOYER_ROLE } from "@/constants/roles";
import {
  REPORT_TAB_SERVICE_TYPE,
  COS_SKILLED_WORKER_INSIDE_SERVICE,
  COS_SKILLED_WORKER_SERVICE,
} from "@/constants/services";

import {
  ASSESSMENT_REPORT_STATUSES,
  MEDIUM_RISK_STATUS,
  LOW_RISK_STATUS,
  HIGH_RISK_STATUS,
  EMPLOYER_INSIDE_WIDGET_TYPE,
  EMPLOYER_OUTSIDE_WIDGET_TYPE,
} from "@/constants/assessment";

import WidgetTemplate from "@/components/Widgets/Templates/WidgetTemplate.vue";
import BackgroundImageForLowRiskWidget from "@/assets/images/widgets/background-2.png";
import LowRiskIconImage from "@/assets/images/widgets/star.png";
import MediumRiskIconImage from "@/assets/images/widgets/warning.png";
import HighRiskIconImage from "@/assets/images/widgets/error.png";
import ArchieveIconImage from "@/assets/images/widgets/archieve.png";

const DEFAULT_WIDGET_TYPE = "default";
const WARNING_WIDGET_TYPE = "warning";
const ERROR_WIDGET_TYPE = "error";

type WidgetKeyObject = {
  [key: string]: any;
};

const REPORT_PARSE_TYPE = "html";

const WIDGET_TYPES = {
  [LOW_RISK_STATUS]: DEFAULT_WIDGET_TYPE,
  [MEDIUM_RISK_STATUS]: WARNING_WIDGET_TYPE,
  [HIGH_RISK_STATUS]: ERROR_WIDGET_TYPE,
};

const ICON_TOP_POSITION = "icon-top";
const ICON_LEFT_POSITION = "icon-left";

const WIDGET_ICON_POSITIONS = {
  [LOW_RISK_STATUS]: ICON_TOP_POSITION,
  [MEDIUM_RISK_STATUS]: ICON_LEFT_POSITION,
  [HIGH_RISK_STATUS]: ICON_LEFT_POSITION,
};

const WIDGET_ICON_IMAGES = {
  [LOW_RISK_STATUS]: LowRiskIconImage,
  [MEDIUM_RISK_STATUS]: MediumRiskIconImage,
  [HIGH_RISK_STATUS]: HighRiskIconImage,
};

const WIDGET_BACKGROUNDS = {
  [LOW_RISK_STATUS]: BackgroundImageForLowRiskWidget,
  [MEDIUM_RISK_STATUS]: "",
  [HIGH_RISK_STATUS]: "",
};

const ALIGN = "center";

const REPORT_ROUTE = "CandidateTemplateReport";

const ALIAS = "ca_report_candidate_assessment";

@Component({
  components: {
    WidgetTemplate,
    PopupSkilledWorkerVisaService: () =>
      import("@/modules/Compliance/Popups/PopupSkilledWorkerVisaService.vue"),
    SubmitRequestVisaPopUp: () => import("@/components/Popups/SubmitRequestVisaPopUp.vue"),
    ArchiveUnavailablePopup: () => import("@/components/Popups/ArchiveUnavailablePopup.vue"),
    NewWidgetSkilledWorkerVisa: () =>
      import("@/components/V2/WidgetSkilledWorkerVisa/NewWidgetSkilledWorkerVisa.vue"),
  },
})
export default class RiskEmployerWidget extends Vue {
  @Prop({ type: String, default: LOW_RISK_STATUS }) readonly risk?: string;
  @Prop({ type: Number, default: 0 }) readonly childServiceId?: number;

  archieveIconImage = ArchieveIconImage;

  widgetTypes: WidgetKeyObject = WIDGET_TYPES;
  widgetIconImages: WidgetKeyObject = WIDGET_ICON_IMAGES;
  widgetIconPositions: WidgetKeyObject = WIDGET_ICON_POSITIONS;
  widgetBackgrounds: WidgetKeyObject = WIDGET_BACKGROUNDS;

  align = ALIGN;

  reportStatuses = ASSESSMENT_REPORT_STATUSES;

  get level() {
    if (!this.risk) return LOW_RISK_STATUS;

    const levels = this.reportStatuses as any;

    return levels[this.risk];
  }

  get caseId(): number {
    return Number(this.$route.params.id ?? 0);
  }

  get candidateName() {
    return this.report?.user_name || "your candidate";
  }

  get MESSAGES() {
    return {
      employer: {
        levels: {
          low_risk: {
            title: "Low or no risk",
            text:
              `${this.candidateName} is eligible for a Skilled Worker visa!\n` +
              "View the detailed report containing immigration fees and approximate timeline.",
          },
          medium_risk: {
            title: "Medium risk",
            text:
              `View the detailed report on what you and ${this.candidateName} can do\n` +
              "to become eligible for a Skilled Worker visa.",
          },
          high_risk: {
            title: "High risk",
            text:
              `Unfortunately, ${this.candidateName} is not currently eligible for a Skilled Worker visa, \n` +
              "and the risk of visa rejection by the Home Office is high. Please check the \n" +
              "Eligibility Report to find out what actions can be taken to ensure your candidate's eligibility.",
          },
        },
        labels: {
          status: "Status",
        },
        button: {
          viewFullReport: "View full report",
          startSW: "Start visa application",
          openSW: "Go to visa case",
        },
      },
      candidate: {
        levels: {
          low_risk: {
            title: "Low or no risk",
            text:
              "Congratulations! You are eligible for a Skilled Worker visa! \n" +
              "View the detailed report containing immigration fees and approximate timeline.",
          },
          medium_risk: {
            title: "Medium risk",
            text:
              "View the detailed report on what you can do to become \n" +
              "eligible for a Skilled Worker visa.",
          },
          high_risk: {
            title: "High risk",
            text:
              "Unfortunately, you are not currently eligible for a Skilled Worker visa, \n" +
              "and the risk of visa rejection by the Home Office is high. \n" +
              "Please check the Eligibility Report to find out what actions \n" +
              "you can take to become eligible for the visa.",
          },
        },
        labels: {
          status: "Status",
        },
        button: {
          viewFullReport: "View full report",
          startSW: "",
          openSW: "Go to visa case",
        },
      },
    };
  }

  get messages() {
    if (SMUser.user?.role === EMPLOYER_ROLE) {
      return this.MESSAGES.employer;
    }

    return this.MESSAGES.candidate;
  }

  get subtitle() {
    if (!this.level) return "";

    const levels = this.messages.levels as any;

    return `${this.messages.labels.status}: ${levels[this.level].title}`;
  }

  get eligibilityStatus() {
    if (!this.level) return "";

    const levels = this.messages.levels as any;

    return levels[this.level].title;
  }

  get text() {
    if (!this.level) return "";

    const levels = this.messages.levels as any;

    return levels[this.level].text;
  }

  get type() {
    if (!this.level) return this.widgetTypes.low_risk;

    return this.widgetTypes[this.level];
  }

  get iconImage() {
    if (!this.level) return this.widgetIconImages.low_risk;

    return this.widgetIconImages[this.level];
  }

  get iconPosition() {
    if (!this.level) return this.widgetIconPositions.low_risk;

    return this.widgetIconPositions[this.level];
  }

  get backgroundImage() {
    if (!this.level) return this.widgetBackgrounds.low_risk;

    return this.widgetBackgrounds[this.level];
  }

  showSkilledWorkerPopup = true;

  openSkilledWorkerPopup() {
    // TO DO: refactor
    this.$root.$emit("open-start-sw-popup");
    // this.showSkilledWorkerPopup = true;
  }

  closeSkilledWorkerPopup() {
    this.showSkilledWorkerPopup = false;

    this.openSubmitRequestVisaPopup();
  }

  submitRequestVisaPopup = false;

  openSubmitRequestVisaPopup(): void {
    this.submitRequestVisaPopup = true;
  }

  closeSubmitRequestVisaPopup(): void {
    this.submitRequestVisaPopup = false;
  }

  report: API_TReport = {
    content: "",
    eligibility_result: "",
    user_name: "",
    widget_type: "",
    has_child_case: false,
    is_sl_started: false,
    if_sponsor: false,
  };

  get isStartVisaApplicationButtonDisabled(): boolean {
    return !SMUser.isSponsorConfirmation;
  }

  get isSponsor() {
    return this.report.if_sponsor;
  }

  get isInside() {
    return this.report.widget_type == EMPLOYER_INSIDE_WIDGET_TYPE;
  }

  get isOutside() {
    return this.report.widget_type == EMPLOYER_OUTSIDE_WIDGET_TYPE;
  }

  get isEligible() {
    return (
      this.report.eligibility_result == LOW_RISK_STATUS ||
      this.report.eligibility_result == MEDIUM_RISK_STATUS
    );
  }

  get showStartVisaApplicationButton() {
    return !this.childServiceId && this.isSponsor && this.isEligible;
  }

  async openService(serviceId: number) {
    if (!serviceId) return;

    await this.$router.push(`/cases/${serviceId}/dashboard/${SMCases.tabId}`);
  }

  get serviceId(): number {
    return Number(this.$route.params.id ?? 0);
  }

  loading = false;

  async created() {
    await this.fetchServiceReport();
  }

  async fetchServiceReport() {
    const { serviceId } = this;

    if (!serviceId) return;

    try {
      this.loading = true;

      const url = API_ROUTES.CASE_REPORT_PARSE(this.serviceId, ALIAS, REPORT_PARSE_TYPE);

      const { data } = await this.axios.get(url);

      if (!data?.content) return;

      this.report = { ...data };
    } finally {
      this.loading = false;
    }
  }

  get cosType(): string {
    if (this.isInside) return COS_SKILLED_WORKER_INSIDE_SERVICE;

    if (this.isOutside) return COS_SKILLED_WORKER_SERVICE;

    return "";
  }

  viewFullReport() {
    const reportTab = SMCases.caseTabs.find((tab) => tab.tab_type === REPORT_TAB_SERVICE_TYPE);

    if (!reportTab?.tab_type_id) return;

    this.$router.push({
      name: REPORT_ROUTE,
      query: {
        tabTypeId: reportTab.tab_type_id.toString(),
        report_template_alias: reportTab.report_template_alias,
      },
    });
  }

  get isArchived() {
    return SMCases.isArchived;
  }

  unarchivePopup = false;

  openUnArchivePopup() {
    this.unarchivePopup = true;
  }

  closeUnArchivePopup() {
    this.unarchivePopup = false;
  }

  get unarchiveEndpoint(): string {
    if (!this.caseId) return "";

    if (SMUser.isExpert) return API_ROUTES.EXPERT_CASE_UNARCHIVE(this.caseId);

    if (SMUser.isEmployer) return API_ROUTES.CASE_UNARCHIVE(this.caseId);

    return "";
  }

  unarchiveLoading = false;

  async unarchiveCandidate() {
    try {
      this.unarchiveLoading = true;

      await this.axios.get(this.unarchiveEndpoint);

      SMCases.setIsArchived(false);
    } catch (e) {
      this.openUnArchivePopup();
    } finally {
      this.unarchiveLoading = false;
    }
  }
}
